// Territoria Development Roadmap — single source of truth.
// Phase statuses: "complete" | "in-progress" | "early-development" | "planned"
// Feature statuses: "completed" | "in-progress" | "future"

export const ROADMAP_LEGEND = [
  { key: "completed",   label: "Completed",   color: "#5fd99a" },
  { key: "in-progress", label: "In Progress", color: "#f3c552" },
  { key: "future",      label: "Planned",     color: "#8a9bb5" },
];

export const STATUS_COLOR = {
  completed:    "#5fd99a",
  "in-progress": "#f3c552",
  future:       "#8a9bb5",
};

// Phase-level status pill config (separate from feature status colors).
export const PHASE_STATUS = {
  complete:           { label: "Complete",           color: "#5fd99a", glow: true },
  "in-progress":      { label: "In Progress",        color: "#f3c552", glow: true },
  "early-development": { label: "Early Development", color: "#66e6ff", glow: true },
  planned:            { label: "Planned",            color: "#8a9bb5", glow: false },
};

// Maps a phase status -> the dominant accent color used for the milestone dot,
// progress-bar, side stripe, etc.
export const PHASE_ACCENT = {
  complete:           "#5fd99a",
  "in-progress":      "#f3c552",
  "early-development": "#66e6ff",
  planned:            "#8a9bb5",
};

export const ROADMAP_PHASES = [
  {
    number: 1,
    title: "Foundations of Conflict",
    status: "complete",
    pct: 100,
    description: "Build the core systems that power warfare and territorial control.",
    goal: "Enable players to join nations, participate in conflicts, and influence the evolving world map.",
    features: [
      { name: "Global Territory System",     status: "completed" },
      { name: "Nation Identity & Citizenship", status: "completed" },
      { name: "Campaign Warfare",            status: "completed" },
      { name: "Combat Systems",              status: "completed" },
      { name: "Regional Control",            status: "completed" },
      { name: "Rankings & Profiles",         status: "completed" },
    ],
  },
  {
    number: 2,
    title: "Economy & Logistics",
    status: "in-progress",
    pct: 40,
    description: "Establish the economic systems that fuel nations and warfare.",
    goal: "Create a player-driven economy where resources, logistics, and industry become strategic assets.",
    features: [
      { name: "Resource Production",     status: "in-progress" },
      { name: "Marketplace & Trade",     status: "in-progress" },
      { name: "Inventory & Equipment",   status: "in-progress" },
      { name: "Transportation Networks", status: "future" },
      { name: "Immigration & Travel",    status: "future" },
      { name: "Economic Progression",    status: "in-progress" },
    ],
  },
  {
    number: 3,
    title: "Nations & Governance",
    status: "early-development",
    pct: 10,
    description: "Introduce leadership, governance, and national decision-making.",
    goal: "Allow citizens to shape the direction, priorities, and future of their nation.",
    features: [
      { name: "Elections & Government", status: "future" },
      { name: "National Treasury",      status: "future" },
      { name: "Taxation Systems",       status: "future" },
      { name: "Laws & Policies",        status: "future" },
      { name: "Strategic Command",      status: "future" },
      { name: "National Development",   status: "future" },
    ],
  },
  {
    number: 4,
    title: "Coalitions & Diplomacy",
    status: "planned",
    pct: 0,
    description: "Forge alliances and reshape the geopolitical landscape between nations.",
    goal: "Create a geopolitical landscape where nations cooperate, compete, and shape global power balances.",
    features: [
      { name: "Coalition Formation",      status: "future" },
      { name: "Diplomatic Agreements",    status: "future" },
      { name: "International Cooperation", status: "future" },
      { name: "Strategic Partnerships",   status: "future" },
      { name: "Coalition Objectives",     status: "future" },
      { name: "Global Influence",         status: "future" },
    ],
  },
  {
    number: 5,
    title: "Information & Intelligence",
    status: "planned",
    pct: 0,
    description: "Expand the battlefield beyond combat into narratives and intel.",
    goal: "Give players the ability to shape narratives, coordinate strategy, and influence public opinion.",
    features: [
      { name: "Media Networks",        status: "future" },
      { name: "War Reporting",         status: "future" },
      { name: "National Broadcasting", status: "future" },
      { name: "Intelligence Systems",  status: "future" },
      { name: "Historical Archives",   status: "future" },
      { name: "Information Operations", status: "future" },
    ],
  },
  {
    number: 6,
    title: "National Development",
    status: "planned",
    pct: 0,
    description: "Turn nations into living entities shaped by long-term investment.",
    goal: "Transform nations into living entities shaped by player decisions and investment.",
    features: [
      { name: "Infrastructure Systems",  status: "future" },
      { name: "Industrial Development",  status: "future" },
      { name: "Strategic Investments",   status: "future" },
      { name: "Regional Improvements",   status: "future" },
      { name: "Long-Term Growth",        status: "future" },
      { name: "National Specialization", status: "future" },
    ],
  },
  {
    number: 7,
    title: "Global Expansion",
    status: "planned",
    pct: 0,
    description: "Prepare Territoria for worldwide launch and long-term growth.",
    goal: "Deliver Territoria as a fully accessible persistent geopolitical strategy MMO across desktop and mobile platforms.",
    features: [
      { name: "Performance Optimization",  status: "future" },
      { name: "Public Release",            status: "future" },
      { name: "Steam Integration",         status: "future" },
      { name: "Mobile Support",            status: "future" },
      { name: "Cross-Platform Progression", status: "future" },
      { name: "Scalability Improvements",  status: "future" },
    ],
  },
];

export const LONG_TERM_VISION = {
  intro:
    "Territoria is a persistent geopolitical strategy MMO where players shape the world through warfare, economics, politics, and information.",
  pillars: [
    "Every battle changes borders.",
    "Every election changes leadership.",
    "Every resource powers industry.",
    "Every citizen shapes history.",
  ],
};

// Per-phase progress. Uses explicit `pct` if provided, otherwise computes from features.
export function phaseProgress(phase) {
  const features = phase?.features || [];
  const total = features.length;
  const done  = features.filter(f => f.status === "completed").length;
  const wip   = features.filter(f => f.status === "in-progress").length;
  const pct = typeof phase?.pct === "number" ? phase.pct : (total ? Math.round((done / total) * 100) : 0);
  return { total, done, wip, pct };
}

// Overall progress is the average of phase percentages.
// Accepts an optional phases array (e.g. from API); falls back to the static default.
export function overallProgress(phases) {
  const list = phases && phases.length ? phases : ROADMAP_PHASES;
  if (!list.length) return { pct: 0, totalFeatures: 0, shipped: 0, inBuild: 0, phaseCount: 0 };
  const avg = list.reduce((acc, p) => acc + phaseProgress(p).pct, 0) / list.length;
  const allFeatures = list.flatMap(p => p.features || []);
  return {
    pct: Math.round(avg),
    totalFeatures: allFeatures.length,
    shipped: allFeatures.filter(f => f.status === "completed").length,
    inBuild: allFeatures.filter(f => f.status === "in-progress").length,
    phaseCount: list.length,
  };
}
